import { getCompany } from "@/api/company/get-Company";
import { createService } from "@/api/services/create-Services";
import { queryClient } from "@/lib/reactQuery";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { useSelectedServiceState } from "./servicesStates";
import { useEffect } from "react";

const createServiceSchema = z.object({
  serviceName: z.string().min(1, "Service name is required"),
  // if no number is input save as null in db (price and serviceQuantity)
  serviceQuantity: z.preprocess(
    (val) => (val === "" ? undefined : val),
    z.coerce.number().optional(),
  ),
  price: z.preprocess(
    (val) => (val === "" ? undefined : val),
    z.coerce.number().optional(),
  ),
  buildingsId: z.string().optional(),
});

type CreateServiceType = z.infer<typeof createServiceSchema>;

export const useCreateService = () => {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<CreateServiceType>({
    resolver: zodResolver(createServiceSchema),
  });

  const { selectedService } = useSelectedServiceState();

  const { mutateAsync: createServiceFn } = useMutation({
    mutationKey: ["services"],
    mutationFn: createService,
    onSuccess: () => {
      toast.success("Service created successfully");
      queryClient.invalidateQueries({ queryKey: ["services"] });
    },
    onError: () => {
      toast.error("Error creating service");
    },
  });

  const { data: companyData, isLoading: isCompanyLoading } = useQuery({
    queryKey: ["company"],
    queryFn: getCompany,
    staleTime: 10 * 60 * 1000,
  });

  // used to clear fields when finished an edit
  useEffect(() => {
    if (selectedService) {
      reset({
        serviceName: selectedService.serviceName,
        serviceQuantity: selectedService.serviceQuantity || undefined,
        price: selectedService.price || undefined,
      });
    } else {
      reset({
        serviceName: "",
        serviceQuantity: undefined,
        price: undefined,
      });
    }
  }, [selectedService, reset]);

  return {
    // Form controls
    control,
    register,
    handleSubmit,
    errors,
    isSubmitting,
    reset,

    // API operations
    createServiceFn,
    companyData,
    isCompanyLoading,

    //schema
    createServiceSchema,
  };
};
